import { forwardRef } from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FOUNDER_CALL_LABEL, FOUNDER_CALL_URL } from "@shared/founderCall";

/**
 * The URL and label live in @shared/founderCall so the /features/* content
 * files (which are shared, not client, modules) can point at the same source.
 * Re-exported here so UI code can keep importing them from this component.
 */
export { FOUNDER_CALL_LABEL, FOUNDER_CALL_URL };

type FounderCallButtonProps = Omit<ButtonProps, "asChild" | "onClick"> & {
  /** Override the visible label. Defaults to the standard CTA wording. */
  label?: string;
};

/**
 * Renders the founder-call CTA as a real anchor rather than a button that calls
 * `window.open`. Popup blockers swallow programmatic `window.open` calls, and an
 * anchor also gives middle-click, "open in new tab" and crawlable markup for free.
 */
export const FounderCallButton = forwardRef<HTMLAnchorElement, FounderCallButtonProps>(
  ({ label = FOUNDER_CALL_LABEL, className, ...buttonProps }, ref) => (
    <Button {...buttonProps} className={className} asChild>
      <a ref={ref} href={FOUNDER_CALL_URL} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    </Button>
  ),
);
FounderCallButton.displayName = "FounderCallButton";

/** Plain-text variant for inline/secondary placements. */
export function FounderCallLink({
  className,
  children,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a
      {...rest}
      href={FOUNDER_CALL_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={cn("underline underline-offset-4 transition-colors", className)}
    >
      {children ?? FOUNDER_CALL_LABEL}
    </a>
  );
}
